/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import {
  luhnAlgorithmCheck,
  parseCurrencyToValue,
  validateCVV,
  validateExpiryYear,
  validatePaymentZipCode,
} from './FormUtils';
import { currencyFormat, getDueDate } from './CommonUtils';
import {
  FREE_SHIP_TYPES,
  INVOICE_CUSTOM_ITEM_SVG,
  INVOICE_FIELD_ERROR_MSGS,
} from '@/constants/Invoice';
import { fullNameRegex, validateName } from '@/components/utils/FunctionUtils';
import type {
  IInvoice,
  IInvoiceObject,
  IPaymentInfo,
  IPaymentPayload,
  ISummaryItemProps,
  InvoiceItem,
} from '@/common/interfaces/invoice.interface';
import type { IUser } from '@/common/interfaces/user.interface';
import type { TagColorThemes } from '@/components/utils/TypeUtils';

/** Method to validate the credit card details */
export const validateCreditCardDetails = (field: string, value: string, paymentDue?: number) => {
  /** touchedFields.hasOwnProperty('creditCard') */
  /** I need that comment as I need to update the validation If a form field is touched. */

  if (field === 'creditCard') {
    const isCreditCardValid = luhnAlgorithmCheck(value);

    if (!isCreditCardValid) {
      return INVOICE_FIELD_ERROR_MSGS.CREDIT_CARD_INCORRECT;
    }
  }

  if (field === 'expiryDate') {
    const isExpiryValid = validateExpiryYear(value);

    if (!isExpiryValid) {
      return INVOICE_FIELD_ERROR_MSGS.EXPIRY_DATE;
    }
  }

  if (field === 'cvv') {
    const isCvvValid = validateCVV(value);

    if (!isCvvValid) {
      return INVOICE_FIELD_ERROR_MSGS.CVV;
    }
  }

  if (field === 'zipCode') {
    const isZipcodeValid = validatePaymentZipCode(value);

    if (!isZipcodeValid) {
      return INVOICE_FIELD_ERROR_MSGS.ZIPCODE;
    }
  }

  if (field === 'amount') {
    const amount = parseCurrencyToValue(value);

    if (Number(amount) > Number(paymentDue)) {
      return "That's more than the due amount, silly.";
    }

    if (Number(amount) === 0) {
      return 'Please enter an amount greater than 0.';
    }
  }

  return true;
};

/**
 * A method to validate the full name.
 *
 * @param value - The full name to validate.
 */
export const nameValidation = (value: string) => {
  if (!fullNameRegex.test(value)) return INVOICE_FIELD_ERROR_MSGS.FULLNAME;
  if (!validateName(value)) return INVOICE_FIELD_ERROR_MSGS.FULLNAME;

  return true;
};

/**
 * A method to validate the invoice form fields on blur.
 *
 * @param field - The field name to validate.
 * @param message - The message to display.
 * @param setError - Callback to show the error message.
 */
/** I had to use any here as setError is a callback method and it had other props in it. */
export const validateFormFieldsOnBlur = (
  field: { name: string; value: string },
  message: string,
  setError: any
) => {
  if (field.name === 'creditCard') {
    if (!luhnAlgorithmCheck(field.value)) {
      setError(field.name, {
        type: 'manual',
        message: field.value ? message : INVOICE_FIELD_ERROR_MSGS.CREDIT_CARD,
      });
    }
  } else if (field.name === 'expiryDate') {
    if (!validateExpiryYear(field.value)) {
      setError(field.name, {
        type: 'manual',
        message: field.value ? message : INVOICE_FIELD_ERROR_MSGS.EXPIRY_DATE,
      });
    }
  } else if (field.name === 'cvv') {
    if (!validateCVV(field.value)) {
      setError(field.name, {
        type: 'manual',
        message: field.value ? message : INVOICE_FIELD_ERROR_MSGS.CVV,
      });
    }
  } else if (field.name === 'zipCode') {
    if (!validatePaymentZipCode(field.value)) {
      setError(field.name, {
        type: 'manual',
        message: field.value ? message : INVOICE_FIELD_ERROR_MSGS.ZIPCODE,
      });
    }
  }
};

/**
 * Method to return the payload for the invoive payment.
 *
 * @param details - Payment form information.
 * @param invoice - Invoice details
 * @param user - User details
 */
export const getInvoicePaymentPayload = (
  details: IPaymentInfo,
  invoice: IInvoice,
  user?: IUser
) => {
  const cardNumber = details.creditCard.replace(/\s/g, '');
  const expMonthYear = details.expiryDate.split('/');
  const expirationDate = `${expMonthYear[0]}/${String(expMonthYear[1]).slice(-2)}`;

  const payload: IPaymentPayload = {
    paymentMethod: 1,
    amount: Number(details.amount),
    cardNumber,
    cardCode: details.cvv,
    expirationDate,
    zipCode: details.zipCode,
    notes: null,
    invoiceId: invoice.invoiceId,
    payerName: details.name,
    payerEmail: user ? user.email : '',
    authOnly: false,
  };

  return payload;
};

/**
 * Method to get the invoice payment status details(paid, unpaid, overdue and partially paid.)
 *
 * @param invoice - The invoice object.
 */
export const getInvoicePaymentDetails = (invoice: IInvoiceObject) => {
  const paymentExpected = Number(invoice.paymentExpected);
  const paymentReceived = Number(invoice.paymentReceived);

  const daysLeft = getDueDate(invoice.dueDate);

  const paymentStatus = {
    statusTag: { text: '', theme: 'success' as TagColorThemes },
    daysLeftMessage: '',
  };

  if (daysLeft < 0 && paymentExpected > paymentReceived) {
    paymentStatus.statusTag = { text: 'Overdue', theme: 'danger' };
    paymentStatus.daysLeftMessage = `${Math.abs(daysLeft)} ${daysLeft === -1 ? 'day' : 'days'}`;
  } else if (daysLeft <= 3 && paymentExpected > paymentReceived && paymentReceived === 0) {
    paymentStatus.statusTag = { text: 'Unpaid', theme: 'danger' };
    paymentStatus.daysLeftMessage = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
  } else if (paymentExpected > paymentReceived && paymentReceived !== 0) {
    paymentStatus.statusTag = { text: 'Partially Paid', theme: 'alt-primary' };
    paymentStatus.daysLeftMessage = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
  } else if (paymentExpected === paymentReceived || paymentReceived > paymentExpected) {
    paymentStatus.statusTag = { text: 'Paid', theme: 'success' };
    paymentStatus.daysLeftMessage = '';
  } else {
    paymentStatus.statusTag = { text: 'Unpaid', theme: 'alt-primary' };
    paymentStatus.daysLeftMessage = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
  }

  return paymentStatus;
};

/**
 * Function to map invoice items to summary item props
 * @param invoice - The invoice items array
 */
export const getInvoiceSummaryItems = (invoice: InvoiceItem[]) =>
  invoice.map((item) => {
    const { styleCode, imageUrl, orderItemId, quantity, unitPrice, discount, description } = item;
    let primaryImage = imageUrl;
    let brand = '';
    let title = styleCode;

    const discountedPrice = discount ? unitPrice + discount : null;

    /** If there's no order item id then it's a custom invoice item */
    if (!orderItemId) {
      brand = styleCode;
      title = '';
      primaryImage = INVOICE_CUSTOM_ITEM_SVG;
    }

    return {
      imageSrc: primaryImage,
      styleCode: title,
      brand,
      quantity,
      discountedPrice,
      pricePerUnit: unitPrice,
      totalPrice: quantity * unitPrice,
      description,
    } as ISummaryItemProps;
  });

/**
 * Function to get shipping price to show on ui
 * @param shiptypeKey - The shipping selected
 * @param shippingPrice - The shipping price
 */
export const getShippingPrice = (shiptypeKey: string, shippingPrice: number) => {
  const isFreeShipType =
    FREE_SHIP_TYPES.includes(shiptypeKey) && (!shippingPrice || !Number(shippingPrice));

  return isFreeShipType ? 'FREE' : currencyFormat(shippingPrice);
};
